import { useMsal } from "@azure/msal-react";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../../authConfig";
import { getMyRoles } from "../../services/userService";
import { useLoading } from "../common/LoadingContext";
import { useHelpModal } from "../help/HelpModalContext";
import { getRedirectUrl, clearRedirectUrl } from "../../utils/redirectUtils";

const Login = () => {
  const { setIsLoading } = useLoading();
  const [version, setVersion] = useState("");
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [error, setError] = useState({
    message: null,
    articleName: null,
    articleKey: null,
  });
  const [navigatingToRoot, setNavigatingToRoot] = useState(false);
  const { openHelpModal } = useHelpModal();

  const formattedEnvironment =
    document.title.indexOf("-") !== -1
      ? document.title.substring(document.title.indexOf("-") + 1).toUpperCase()
      : "";

  // Check if this is a demo environment
  const isDemoEnvironment = () => {
    const assistantHostname = process.env.REACT_APP_ASSISTANT_EXTERNAL_HOSTNAME;
    const currentHostname = window.location.hostname;

    return (
      assistantHostname === "ai.assistant.lumeris.com" ||
      assistantHostname === "ai.agent.lumeris.com" ||
      currentHostname === "ai.assistant.lumeris.com" ||
      currentHostname === "ai.agent.lumeris.com"
    );
  };

  const showDemoBanner = isDemoEnvironment();

  useEffect(() => {
    fetch("/package-meta.json")
      .then((response) => response.json())
      .then((data) => {
        setVersion(`Build: ${data.v}`);
      })
      .catch((error) => console.error("Error fetching version data:", error));
  }, []);

  useEffect(() => {
    const checkUserAndRoles = async () => {
      if (accounts.length > 0) {
        try {
          setNavigatingToRoot(true);
          setTimeout(async () => {
            const roles = await getMyRoles();
            const redirectUrl = getRedirectUrl();
            if (
              roles.length > 0 ||
              redirectUrl.toLowerCase() === "/champions"
            ) {
              clearRedirectUrl();
              setIsLoading(false);
              navigate(redirectUrl);
            } else {
              setError({
                message: "You don't have any assigned roles.",
                articleName: "How to request access",
                articleKey: "request_access",
              });
              setNavigatingToRoot(false);
            }
          }, 500);
        } catch (error) {
          console.error("Error fetching user roles:", error);
          setError({
            message:
              "An error occurred while checking your roles. Please try again.",
            articleName: null,
            articleKey: null,
          });
          setNavigatingToRoot(false);
        }
      }
    };
    checkUserAndRoles();
  }, [accounts, navigate, instance, setIsLoading]);

  useEffect(() => {
    if (
      inProgress === "login" ||
      inProgress === "startup" ||
      inProgress === "handleRedirect" ||
      navigatingToRoot
    ) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [inProgress, navigatingToRoot, setIsLoading]);

  const handleLogin = () => {
    instance
      .loginRedirect(loginRequest)
      .then(() => {
        setNavigatingToRoot(true);
      })
      .catch((e) => {
        console.error("Login failed", e);
        setError({
          message: "Login failed. Please try again.",
          articleName: null,
          articleKey: null,
        });
      });
  };

  const handleNeedHelp = () => {
    if (error.articleKey) {
      openHelpModal(error.articleKey);
    }
  };

  if (
    inProgress === "login" ||
    inProgress === "startup" ||
    inProgress === "handleRedirect" ||
    navigatingToRoot
  ) {
    return null;
  }

  return (
    <div>
      {/* Demo Environment Banner */}
      {showDemoBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black px-4 py-3 shadow-lg border-b-2 border-yellow-600">
          <div className="flex items-center justify-center max-w-6xl mx-auto">
            <div className="flex items-center space-x-2">
              <span className="text-lg animate-pulse text-red-600">⚠️</span>
              <span className="text-sm font-medium">
                You've reached the demo environment. Please navigate to{" "}
                <a
                  href="https://ai.agent.aida.hcs.cloud"
                  className="underline font-semibold hover:text-yellow-900 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://ai.agent.aida.hcs.cloud
                </a>{" "}
                to use production.
              </span>
            </div>
          </div>
        </div>
      )}

      <div
        className={`card-glass min-w-[450px] max-w-lg w-full space-y-8 p-10 rounded-xl shadow-lg ${
          showDemoBanner ? "mt-16" : ""
        }`}
      >
        {error.message && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-700">{error.message}</p>
                {error.articleName && error.articleKey && (
                  <div className="mt-2">
                    <button
                      onClick={handleNeedHelp}
                      className="text-sm font-medium text-red-700 hover:text-red-600 transition-colors duration-200"
                    >
                      {error.articleName} &rarr;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div>
          {formattedEnvironment && (
            <div className="absolute top-3 right-5 z-10">
              <span style={{ color: "#f0b557", fontWeight: "500" }}>
                {formattedEnvironment}
              </span>
            </div>
          )}
          <img className="mx-auto h-12 w-auto" src="logo.svg" alt="Lumeris" />
          <h2 className="mt-6 text-center text-3xl font-sol-w01 text-stretch-wide text-white">
            {getRedirectUrl() === "/champions"
              ? "Tom Champions"
              : "AI Agent Portal"}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            Please sign in to continue
          </p>
        </div>
        <div>
          <button
            onClick={handleLogin}
            className="group relative w-4/5 left-1/2 translate -translate-x-1/2 flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:scale-105"
          >
            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg
                className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            Log in with SSO
          </button>
        </div>
      </div>
      <div className="absolute text-gray-500 text-xs bottom-0 left-1/2 translate -translate-x-1/2 p-2">
        {version}
      </div>
    </div>
  );
};

export default Login;
